import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';

import BusinessTypeStep, { BUSINESS_TYPES } from './components/BusinessTypeStep';
import BusinessDetailsStep from './components/BusinessDetailsStep';
import SetupCompleteStep from './components/SetupCompleteStep';
import { seedBusinessData } from './components/seedBusinessData';
import toast from 'react-hot-toast';

const STEPS = ['Business Type', 'Your Details', 'Setup Complete'];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { organization, updateOrganization, user } = useAuth();
  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState('');
  const [details, setDetails] = useState({
    businessName: '',
    serviceArea: '',
    teamSize: '',
    referralSource: '',
  });
  const [isSeeding, setIsSeeding] = useState(false);

  useEffect(() => {
    if (organization) {
      setDetails((prev) => ({
        ...prev,
        businessName: organization?.name || '',
      }));
      if (organization?.business_type) {
        setSelectedType(organization?.business_type);
      }
    }
  }, [organization]);

  const handleDetailsChange = (field, value) => {
    setDetails((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    if (step === 0) {
      if (!selectedType) { toast?.error('Please select your business type'); return; }
      setStep(1);
    } else if (step === 1) {
      if (!details?.businessName?.trim()) { toast?.error('Please enter your business name'); return; }
      // Save details to org
      await updateOrganization({
        name: details?.businessName,
        service_area: details?.serviceArea,
        team_size: details?.teamSize,
        settings: { ...organization?.settings, referral_source: details?.referralSource },
      });
      setStep(2);
    }
  };

  const handleComplete = async () => {
    if (!organization?.id) return;
    setIsSeeding(true);
    try {
      // Set trial dates FIRST before seeding — so trial is always saved
      const now = new Date();
      const trialEnd = new Date(now);
      trialEnd?.setDate(trialEnd?.getDate() + 14);

      // Save trial info to org immediately
      const { error: trialError } = await updateOrganization({
        onboarding_completed: true,
        trial_start_date: now?.toISOString(),
        trial_end_date: trialEnd?.toISOString(),
        subscription_status: 'trial',
      });

      if (trialError) {
        console.error('Trial save error:', trialError);
        toast?.error('Could not save trial info. Please try again.');
        return;
      }

      // Then seed business data (non-critical — proceed even if it fails)
      try {
        await seedBusinessData(organization?.id, selectedType, details?.businessName);
      } catch (seedErr) {
        console.warn('Seed data warning (non-critical):', seedErr);
      }

      toast?.success('Your CRM is ready! 14-day free trial started.');
      navigate('/dashboard');
    } catch (err) {
      console.error('Onboarding complete error:', err);
      toast?.error('Setup failed. Please try again.');
    } finally {
      setIsSeeding(false);
    }
  };

  const businessLabel = BUSINESS_TYPES?.find((b) => b?.id === selectedType)?.name || '';

  return (
    <>
      <Helmet>
        <title>Set Up Your CRM — Wilcart</title>
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-center py-6 border-b border-border bg-background/80 backdrop-blur-sm">
          <div className="flex items-center">
            <img
              src="/assets/images/wilcart_logo_1-1775846328647.png"
              alt="Wilcart CRM Logo"
              className="h-10 w-auto object-contain"
            />
            <span className="ml-3 text-xl font-bold text-foreground">Wilcart</span>
          </div>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center py-6 px-4">
          <div className="flex items-center space-x-2">
            {STEPS?.map((label, idx) => (
              <React.Fragment key={idx}>
                <div className="flex items-center space-x-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    idx < step ? 'bg-primary text-primary-foreground' :
                    idx === step ? 'bg-primary text-primary-foreground ring-4 ring-primary/20': 'bg-muted text-muted-foreground'
                  }`}>
                    {idx < step ? (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : idx + 1}
                  </div>
                  <span className={`text-sm hidden sm:block ${
                    idx === step ? 'text-foreground font-medium' : 'text-muted-foreground'
                  }`}>{label}</span>
                </div>
                {idx < STEPS?.length - 1 && (
                  <div className={`w-8 sm:w-16 h-0.5 transition-all ${
                    idx < step ? 'bg-primary' : 'bg-border'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex items-start justify-center px-4 pb-8">
          <div className="w-full max-w-2xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
                className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm"
              >
                {step === 0 && (
                  <BusinessTypeStep
                    selected={selectedType}
                    onSelect={setSelectedType}
                  />
                )}
                {step === 1 && (
                  <BusinessDetailsStep
                    data={details}
                    onChange={handleDetailsChange}
                  />
                )}
                {step === 2 && (
                  <SetupCompleteStep
                    businessType={selectedType}
                    onComplete={handleComplete}
                    isSeeding={isSeeding}
                  />
                )}

                {/* Navigation */}
                {step < 2 && (
                  <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                    <button
                      onClick={() => step > 0 && setStep(step - 1)}
                      className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                        step === 0 ? 'invisible' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }`}
                    >
                      ← Back
                    </button>
                    <button
                      onClick={handleNext}
                      disabled={step === 0 && !selectedType}
                      className="px-6 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {step === 1 ? 'Continue →' : 'Continue →'}
                    </button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingPage;
